import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import {
  api,
  type PitchCount,
  type Game,
  type BulkPitchUpdate,
  type Team,
  type Season,
  type CreateGameRequest,
} from '../../services/api';

@customElement('game-pitch-editor')
export class GamePitchEditor extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: String })
  gameId = '';

  @state()
  private game: Game | null = null;

  @state()
  private pitchCounts: PitchCount[] = [];

  @state()
  private loading = true;

  @state()
  private saving = false;

  // Inline edit state
  @state()
  private editingPlayerId: string | null = null;

  @state()
  private editValue = 0;

  @state()
  private editReason = '';

  // Bulk edit state
  @state()
  private bulkMode = false;

  @state()
  private selectedPlayers: Set<string> = new Set();

  @state()
  private bulkValue = 0;

  @state()
  private bulkReason = '';

  // Toast notifications
  @state()
  private toast: { type: 'success' | 'error'; message: string } | null = null;

  // --- Edit game details modal state ---
  // Modeled on CoachInviteList's edit-email + TeamManager's age-class modals:
  // a fixed-overlay card (NOT DaisyUI <dialog>) matching the rest of the
  // codebase, with focus moved to the name input on open. Lists are loaded
  // lazily the first time the modal opens to avoid paying for them on every
  // GamePitchEditor mount.
  @state()
  private editOpen = false;

  @state()
  private editName = '';

  @state()
  private editDate = ''; // YYYY-MM-DDTHH:mm (datetime-local format)

  @state()
  private editLocation = '';

  @state()
  private editHomeTeamId = '';

  @state()
  private editAwayTeamId = '';

  @state()
  private editSeasonId = '';

  @state()
  private editSaving = false;

  @state()
  private editError = '';

  @state()
  private editTeams: Team[] = [];

  @state()
  private editSeasons: Season[] = [];

  @state()
  private editListsLoaded = false;

  connectedCallback() {
    super.connectedCallback();
  }

  firstUpdated() {
    if (this.gameId) {
      this.loadData();
    }
  }

  updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('gameId') && this.gameId && changedProps.get('gameId') !== undefined) {
      this.loadData();
    }
  }

  @state()
  private loadError = '';

  private async loadData() {
    if (!this.gameId) return;

    this.loading = true;
    this.loadError = '';
    try {
      // Don't fail the whole load if pitches fetch fails — render the
      // game header without a pitch table rather than landing on a bare
      // "Game not found" page. Common: admin clicks a game that was
      // just deleted by another admin or whose pitches haven't been
      // recorded yet.
      const game = await api.getGame(this.gameId);
      this.game = game;
      try {
        const pitches = await api.getGamePitches(this.gameId);
        this.pitchCounts = pitches || [];
      } catch (pitchErr) {
        console.error('Failed to load pitch counts:', pitchErr);
        this.pitchCounts = [];
        this.showToast('error', 'Could not load pitch counts for this game');
      }
    } catch (error) {
      console.error('Failed to load game:', error);
      const msg = error instanceof Error ? error.message : String(error);
      // Surface the actual server message (e.g., "game not found") so
      // the user can tell "the game no longer exists" apart from a
      // generic network blip.
      this.loadError = msg;
      this.game = null;
    } finally {
      this.loading = false;
    }
  }

  private goBackToList() {
    // Bubble an event the parent AdminView listens for to clear
    // selectedGameId and refresh the games list — if the game was
    // deleted by another admin since the list loaded, this clears
    // the stale entry too.
    this.dispatchEvent(new CustomEvent('game-load-failed', {
      bubbles: true,
      composed: true,
      detail: { gameId: this.gameId, message: this.loadError },
    }));
  }

  private showToast(type: 'success' | 'error', message: string) {
    this.toast = { type, message };
    setTimeout(() => {
      this.toast = null;
    }, 3000);
  }

  private async copyGameId() {
    if (!this.gameId) return;
    try {
      await navigator.clipboard.writeText(this.gameId);
      this.showToast('success', 'Game ID copied');
    } catch {
      this.showToast('error', 'Failed to copy game ID');
    }
  }

  private renderGameIdLine() {
    if (!this.gameId) return nothing;
    const short = this.gameId.slice(0, 8);
    return html`
      <p class="text-xs text-base-content/40 font-mono mt-0.5 flex items-center gap-1.5" data-testid="game-id-line">
        <span aria-label="Game ID">ID: ${short}…</span>
        <button
          type="button"
          class="btn btn-ghost btn-xs px-1 min-h-0 h-5 text-base-content/40 hover:text-base-content"
          aria-label="Copy game ID to clipboard"
          title="Copy full game ID"
          data-testid="copy-game-id-btn"
          @click=${this.copyGameId}
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </button>
      </p>
    `;
  }

  // Inline editing
  private startEdit(pitch: PitchCount) {
    if (this.bulkMode) return;
    this.editingPlayerId = pitch.player_id;
    this.editValue = pitch.count;
    this.editReason = '';
  }

  private cancelEdit() {
    this.editingPlayerId = null;
    this.editValue = 0;
    this.editReason = '';
  }

  private async saveEdit() {
    if (!this.editingPlayerId || !this.gameId) return;

    const originalPitch = this.pitchCounts.find(p => p.player_id === this.editingPlayerId);
    if (!originalPitch || originalPitch.count === this.editValue) {
      this.cancelEdit();
      return;
    }

    this.saving = true;
    try {
      await api.bulkUpdatePitches(this.gameId, [{
        player_id: this.editingPlayerId,
        new_count: this.editValue,
        change_reason: this.editReason || undefined,
      }]);

      // Update local state
      this.pitchCounts = this.pitchCounts.map(p =>
        p.player_id === this.editingPlayerId
          ? { ...p, count: this.editValue }
          : p
      );

      this.showToast('success', 'Pitch count updated');
      this.cancelEdit();
    } catch (error) {
      console.error('Failed to update pitch count:', error);
      this.showToast('error', 'Failed to update pitch count');
    } finally {
      this.saving = false;
    }
  }

  // Bulk editing
  private toggleBulkMode() {
    this.bulkMode = !this.bulkMode;
    if (!this.bulkMode) {
      this.selectedPlayers.clear();
      this.bulkValue = 0;
      this.bulkReason = '';
      this.requestUpdate();
    }
  }

  private togglePlayerSelection(playerId: string) {
    if (this.selectedPlayers.has(playerId)) {
      this.selectedPlayers.delete(playerId);
    } else {
      this.selectedPlayers.add(playerId);
    }
    this.requestUpdate();
  }

  private selectAllPlayers() {
    if (this.selectedPlayers.size === this.pitchCounts.length) {
      this.selectedPlayers.clear();
    } else {
      this.pitchCounts.forEach(p => this.selectedPlayers.add(p.player_id));
    }
    this.requestUpdate();
  }

  private async saveBulkEdit() {
    if (this.selectedPlayers.size === 0 || !this.gameId) return;

    const updates: BulkPitchUpdate[] = Array.from(this.selectedPlayers).map(playerId => ({
      player_id: playerId,
      new_count: this.bulkValue,
      change_reason: this.bulkReason || undefined,
    }));

    this.saving = true;
    try {
      await api.bulkUpdatePitches(this.gameId, updates);

      // Update local state
      this.pitchCounts = this.pitchCounts.map(p =>
        this.selectedPlayers.has(p.player_id)
          ? { ...p, count: this.bulkValue }
          : p
      );

      this.showToast('success', `Updated ${this.selectedPlayers.size} player(s)`);
      this.toggleBulkMode();
    } catch (error) {
      console.error('Failed to bulk update:', error);
      this.showToast('error', 'Failed to bulk update pitch counts');
    } finally {
      this.saving = false;
    }
  }

  // --- Edit game details ---

  /**
   * Convert an ISO 8601 timestamp from the API into the `YYYY-MM-DDTHH:mm`
   * format that `<input type="datetime-local">` requires. The input always
   * shows local time, so we build the string from the local-time components
   * rather than slicing the ISO string (which is UTC). Returns '' on null /
   * unparseable input so the field renders empty rather than "Invalid Date".
   */
  private toDateTimeLocal(iso: string | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  /**
   * Lazy-load teams + seasons the first time the edit modal opens. We don't
   * want to pull these on every GamePitchEditor mount — admins overwhelmingly
   * use this view to fix pitch counts, not metadata.
   */
  private async ensureEditLists() {
    if (this.editListsLoaded) return;
    try {
      const [teams, seasons] = await Promise.all([
        api.getTeams(),
        api.getSeasons(),
      ]);
      this.editTeams = teams || [];
      this.editSeasons = seasons || [];
      this.editListsLoaded = true;
    } catch (error) {
      console.error('Failed to load teams/seasons for edit modal:', error);
      // Non-fatal — the modal still opens with whatever current values are
      // already on the game object. Show a toast so Cara knows the dropdowns
      // are stale.
      this.showToast('error', 'Failed to load team/season list');
    }
  }

  private async openEditModal() {
    if (!this.game) return;
    // Pre-fill from current game values. Sending the full payload back on
    // save is intentional (PUT semantics) — unchanged fields just round-trip.
    this.editName = this.game.name || '';
    this.editDate = this.toDateTimeLocal(this.game.game_date);
    this.editLocation = this.game.location || '';
    this.editHomeTeamId = this.game.home_team_id || '';
    this.editAwayTeamId = this.game.away_team_id || '';
    this.editSeasonId = this.game.season_id || '';
    this.editError = '';
    this.editSaving = false;
    this.editOpen = true;

    // Load dropdown data (no-op after first call).
    await this.ensureEditLists();

    // Focus the name input on next paint for keyboard / SR users.
    requestAnimationFrame(() => {
      const input = this.querySelector<HTMLInputElement>('input.edit-game-name');
      input?.focus();
      input?.select();
    });
  }

  private cancelEditModal() {
    if (this.editSaving) return;
    this.editOpen = false;
    this.editError = '';
  }

  private get editCanSave(): boolean {
    if (!this.editOpen || this.editSaving) return false;
    if (!this.editName.trim()) return false;
    if (!this.editDate) return false;
    const d = new Date(this.editDate);
    if (Number.isNaN(d.getTime())) return false;
    return true;
  }

  private async saveEditModal() {
    if (!this.game || !this.editCanSave) return;

    const parsed = new Date(this.editDate);
    if (Number.isNaN(parsed.getTime())) {
      this.editError = 'Invalid date. Please re-enter.';
      return;
    }

    // Full PUT semantics — send every field the backend accepts, even when
    // unchanged. lat/long are intentionally preserved as-is; this admin
    // surface deliberately doesn't expose a map picker.
    const payload: CreateGameRequest = {
      name: this.editName.trim(),
      game_date: parsed.toISOString(),
      location: this.editLocation.trim() || undefined,
      latitude: this.game.latitude,
      longitude: this.game.longitude,
      home_team_id: this.editHomeTeamId || undefined,
      away_team_id: this.editAwayTeamId || undefined,
      season_id: this.editSeasonId || undefined,
    };

    this.editSaving = true;
    this.editError = '';

    try {
      const updated = await api.updateGame(this.game.id, payload);
      // Trust the server response — it carries the canonical timestamps
      // and any normalized team/season names that downstream UI relies on.
      this.game = updated || { ...this.game, ...payload };
      this.editOpen = false;
      this.editSaving = false;
      this.showToast('success', 'Game details updated');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Update failed';
      // 5xx → toast + leave modal open (per spec). 4xx and any other error
      // → inline error in the modal so the admin can correct without
      // re-typing. The request helper throws string-only Errors, so we
      // pattern-match on the status code substring.
      const is5xx = /\b5\d\d\b/.test(message);
      if (is5xx) {
        this.showToast('error', message);
      }
      this.editError = message;
      this.editSaving = false;
    }
  }

  render() {
    if (this.loading) {
      return html`
        <div class="flex justify-center py-8">
          <span class="loading loading-spinner loading-lg"></span>
        </div>
      `;
    }

    if (!this.game) {
      // Distinguish "deleted/unavailable" from a generic null state. Most
      // common reason this fires: another admin deleted the game between
      // when the games list loaded and when this admin clicked it.
      const friendly = this.loadError
        ? this.loadError
        : "We couldn't load this game.";
      return html`
        <div class="alert alert-warning flex flex-col items-start gap-3" data-testid="game-load-failed">
          <div>
            <div class="font-semibold">${friendly}</div>
            <div class="text-sm opacity-80 mt-1">
              The game may have been deleted, or your network may have hiccuped.
              Try going back to the list and selecting it again.
            </div>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-warning"
            @click=${this.goBackToList}
          >
            Back to game list
          </button>
        </div>
      `;
    }

    return html`
      <div class="space-y-4">
        <!-- Header -->
        <div class="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
          <div>
            <h3 class="text-lg font-semibold">${this.game.name}</h3>
            <p class="text-sm text-base-content/70">
              ${this.game.game_date ? new Date(this.game.game_date).toLocaleDateString() : 'No date'}
              ${this.game.location ? ` • ${this.game.location}` : ''}
            </p>
            ${this.renderGameIdLine()}
          </div>
          <div class="flex gap-2 w-full sm:w-auto">
            <button
              class="btn btn-outline btn-sm flex-1 sm:flex-none"
              data-testid="edit-game-details-btn"
              @click=${this.openEditModal}
              aria-label="Edit game details"
              title="Edit game name, date, location, teams, and season"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
              </svg>
              Edit details
            </button>
            <button
              class="btn btn-sm ${this.bulkMode ? 'btn-primary' : 'btn-outline'} flex-1 sm:flex-none"
              @click=${this.toggleBulkMode}
            >
              ${this.bulkMode ? 'Exit Bulk Mode' : 'Bulk Edit'}
            </button>
          </div>
        </div>

        <!-- Bulk edit controls -->
        ${this.bulkMode ? this.renderBulkControls() : nothing}

        <!-- Pitch counts table -->
        ${this.renderPitchTable()}

        <!-- Empty state -->
        ${this.pitchCounts.length === 0 ? html`
          <div class="text-center py-8 text-base-content/70">
            <p>No pitch counts recorded for this game.</p>
            <p class="text-sm mt-2">Use the "Add Player" button above to add players to this game.</p>
          </div>
        ` : nothing}

        <!-- Edit game details modal -->
        ${this.editOpen ? this.renderEditModal() : nothing}

        <!-- Toast notification -->
        ${this.toast ? html`
          <div class="toast toast-end">
            <div class="alert ${this.toast.type === 'success' ? 'alert-success' : 'alert-error'}">
              <span>${this.toast.message}</span>
            </div>
          </div>
        ` : nothing}
      </div>
    `;
  }

  /**
   * Edit-details modal. Same fixed-overlay card pattern as the edit-email
   * modal in CoachInviteList and the age-class modal in TeamManager —
   * backdrop click / Escape / Cancel all close; Save sends a full PUT
   * (game_date, name, location, lat/long, team ids, season id). Stays
   * open on validation / 4xx errors with an inline message so the admin
   * can fix without re-typing. Sticky footer + max-height + overflow on
   * the body so the keyboard doesn't bury the Save button on iOS.
   */
  private renderEditModal() {
    if (!this.game) return nothing;

    const approved = this.game.status === 'approved';
    const trimmedName = this.editName.trim();
    const nameMissing = !trimmedName;
    const dateMissing = !this.editDate;
    const dateInvalid =
      !!this.editDate && Number.isNaN(new Date(this.editDate).getTime());

    return html`
      <div
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-fade-in p-4"
        @click=${(e: Event) => { if (e.target === e.currentTarget) this.cancelEditModal(); }}
        @keydown=${(e: KeyboardEvent) => { if (e.key === 'Escape') this.cancelEditModal(); }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-game-modal-title"
        data-testid="edit-game-modal"
      >
        <div class="card bg-base-100 shadow-2xl w-full max-w-lg max-h-[calc(100vh-2rem)] flex flex-col animate-slide-up">
          <!-- Header (non-scrolling) -->
          <div class="card-body py-4 border-b border-base-300 shrink-0">
            <h3 id="edit-game-modal-title" class="card-title text-lg">Edit game details</h3>
          </div>

          <!-- Scrollable form body -->
          <div class="overflow-y-auto px-6 py-4 flex-1 space-y-3">
            ${approved ? html`
              <div
                class="alert alert-warning text-xs py-2"
                data-testid="edit-game-approved-warning"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
                <span>
                  This game has been approved. Editing will require re-approval by the opposing coach.
                </span>
              </div>
            ` : nothing}

            <label class="form-control w-full">
              <span class="label-text text-xs">Game name *</span>
              <input
                type="text"
                class="input input-bordered input-sm edit-game-name ${nameMissing ? 'input-error' : ''}"
                data-testid="edit-game-name"
                .value=${this.editName}
                @input=${(e: Event) => { this.editName = (e.target as HTMLInputElement).value; if (this.editError) this.editError = ''; }}
                ?disabled=${this.editSaving}
                aria-invalid=${nameMissing ? 'true' : 'false'}
                required
              />
            </label>

            <label class="form-control w-full">
              <span class="label-text text-xs">Game date & time *</span>
              <input
                type="datetime-local"
                class="input input-bordered input-sm ${dateMissing || dateInvalid ? 'input-error' : ''}"
                data-testid="edit-game-date"
                .value=${this.editDate}
                @input=${(e: Event) => { this.editDate = (e.target as HTMLInputElement).value; if (this.editError) this.editError = ''; }}
                ?disabled=${this.editSaving}
                aria-invalid=${dateMissing || dateInvalid ? 'true' : 'false'}
                required
              />
              ${dateInvalid
                ? html`<span class="label-text-alt text-error text-xs mt-1">Invalid date</span>`
                : nothing}
            </label>

            <label class="form-control w-full">
              <span class="label-text text-xs">Location</span>
              <input
                type="text"
                class="input input-bordered input-sm"
                data-testid="edit-game-location"
                placeholder="Optional"
                .value=${this.editLocation}
                @input=${(e: Event) => { this.editLocation = (e.target as HTMLInputElement).value; }}
                ?disabled=${this.editSaving}
              />
            </label>

            <label class="form-control w-full">
              <span class="label-text text-xs">Home team</span>
              <select
                class="select select-bordered select-sm"
                data-testid="edit-game-home-team"
                .value=${this.editHomeTeamId}
                @change=${(e: Event) => { this.editHomeTeamId = (e.target as HTMLSelectElement).value; }}
                ?disabled=${this.editSaving}
              >
                <option value="">(none)</option>
                ${this.editTeams.map(t => html`
                  <option value=${t.id} ?selected=${t.id === this.editHomeTeamId}>${t.name}</option>
                `)}
              </select>
            </label>

            <label class="form-control w-full">
              <span class="label-text text-xs">Away team</span>
              <select
                class="select select-bordered select-sm"
                data-testid="edit-game-away-team"
                .value=${this.editAwayTeamId}
                @change=${(e: Event) => { this.editAwayTeamId = (e.target as HTMLSelectElement).value; }}
                ?disabled=${this.editSaving}
              >
                <option value="">(none)</option>
                ${this.editTeams.map(t => html`
                  <option value=${t.id} ?selected=${t.id === this.editAwayTeamId}>${t.name}</option>
                `)}
              </select>
            </label>

            <label class="form-control w-full">
              <span class="label-text text-xs">Season</span>
              <select
                class="select select-bordered select-sm"
                data-testid="edit-game-season"
                .value=${this.editSeasonId}
                @change=${(e: Event) => { this.editSeasonId = (e.target as HTMLSelectElement).value; }}
                ?disabled=${this.editSaving}
              >
                <option value="">(none)</option>
                ${this.editSeasons.map(s => html`
                  <option value=${s.id} ?selected=${s.id === this.editSeasonId}>${s.name}</option>
                `)}
              </select>
            </label>

            ${this.editError
              ? html`<p id="edit-game-error" data-testid="edit-game-error" class="text-error text-xs">${this.editError}</p>`
              : nothing}
          </div>

          <!-- Sticky footer (won't be hidden by mobile keyboard) -->
          <div class="card-actions justify-end gap-2 px-6 py-4 border-t border-base-300 shrink-0 bg-base-100">
            <button
              class="btn btn-ghost btn-sm"
              data-testid="edit-game-cancel"
              @click=${this.cancelEditModal}
              ?disabled=${this.editSaving}
            >Cancel</button>
            <button
              class="btn btn-primary btn-sm"
              data-testid="edit-game-save"
              @click=${this.saveEditModal}
              ?disabled=${!this.editCanSave}
            >
              ${this.editSaving
                ? html`<span class="loading loading-spinner loading-sm"></span>`
                : 'Save'}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderBulkControls() {
    return html`
      <div class="card bg-base-200">
        <div class="card-body py-4">
          <div class="flex flex-wrap gap-4 items-end">
            <div class="form-control">
              <label class="label py-1">
                <span class="label-text">Set all selected to:</span>
              </label>
              <input
                type="number"
                class="input input-bordered input-sm w-24"
                min="0"
                .value=${this.bulkValue.toString()}
                @input=${(e: Event) => this.bulkValue = parseInt((e.target as HTMLInputElement).value) || 0}
              />
            </div>
            <div class="form-control flex-1 min-w-48">
              <label class="label py-1">
                <span class="label-text">Reason (optional)</span>
              </label>
              <input
                type="text"
                class="input input-bordered input-sm"
                placeholder="e.g., Scorekeeper correction"
                .value=${this.bulkReason}
                @input=${(e: Event) => this.bulkReason = (e.target as HTMLInputElement).value}
              />
            </div>
            <button
              class="btn btn-primary btn-sm"
              ?disabled=${this.selectedPlayers.size === 0 || this.saving}
              @click=${this.saveBulkEdit}
            >
              ${this.saving
                ? html`<span class="loading loading-spinner loading-sm"></span>`
                : `Apply to ${this.selectedPlayers.size} player(s)`}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderPitchTable() {
    return html`
      <div class="overflow-x-auto">
        <table class="table">
          <thead>
            <tr>
              ${this.bulkMode ? html`
                <th>
                  <label>
                    <input
                      type="checkbox"
                      class="checkbox checkbox-sm"
                      .checked=${this.selectedPlayers.size === this.pitchCounts.length && this.pitchCounts.length > 0}
                      @change=${this.selectAllPlayers}
                    />
                  </label>
                </th>
              ` : nothing}
              <th>#</th>
              <th>Player</th>
              <th class="text-center">Pitch Count</th>
              ${!this.bulkMode ? html`<th class="text-right">Actions</th>` : nothing}
            </tr>
          </thead>
          <tbody>
            ${this.pitchCounts.map(pitch => this.renderPitchRow(pitch))}
          </tbody>
        </table>
      </div>
    `;
  }

  private renderPitchRow(pitch: PitchCount) {
    const isEditing = this.editingPlayerId === pitch.player_id;

    return html`
      <tr class="${isEditing ? 'bg-base-200' : ''}">
        ${this.bulkMode ? html`
          <td>
            <label>
              <input
                type="checkbox"
                class="checkbox checkbox-sm"
                .checked=${this.selectedPlayers.has(pitch.player_id)}
                @change=${() => this.togglePlayerSelection(pitch.player_id)}
              />
            </label>
          </td>
        ` : nothing}
        <td class="font-mono text-base-content/70">${pitch.jersey_number ?? '-'}</td>
        <td class="font-semibold">${pitch.player_name || 'Unknown'}</td>
        <td class="text-center">
          ${isEditing ? html`
            <div class="flex items-center justify-center gap-2">
              <input
                type="number"
                class="input input-bordered input-sm w-20 text-center"
                min="0"
                .value=${this.editValue.toString()}
                @input=${(e: Event) => this.editValue = parseInt((e.target as HTMLInputElement).value) || 0}
                @keydown=${(e: KeyboardEvent) => {
                  if (e.key === 'Enter') this.saveEdit();
                  if (e.key === 'Escape') this.cancelEdit();
                }}
              />
            </div>
          ` : html`
            <span
              class="badge badge-lg font-mono cursor-pointer hover:badge-primary transition-colors ${this.bulkMode ? 'cursor-default' : ''}"
              @click=${() => !this.bulkMode && this.startEdit(pitch)}
              title="${this.bulkMode ? '' : 'Click to edit'}"
            >
              ${pitch.count}
            </span>
          `}
        </td>
        ${!this.bulkMode ? html`
          <td class="text-right">
            ${isEditing ? html`
              <div class="flex items-center justify-end gap-2">
                <input
                  type="text"
                  class="input input-bordered input-sm w-40"
                  placeholder="Reason (optional)"
                  .value=${this.editReason}
                  @input=${(e: Event) => this.editReason = (e.target as HTMLInputElement).value}
                />
                <button
                  class="btn btn-ghost btn-xs"
                  @click=${this.cancelEdit}
                  ?disabled=${this.saving}
                >
                  Cancel
                </button>
                <button
                  class="btn btn-primary btn-xs"
                  @click=${this.saveEdit}
                  ?disabled=${this.saving}
                >
                  ${this.saving ? html`<span class="loading loading-spinner loading-xs"></span>` : 'Save'}
                </button>
              </div>
            ` : html`
              <button
                class="btn btn-ghost btn-xs"
                @click=${() => this.startEdit(pitch)}
              >
                Edit
              </button>
            `}
          </td>
        ` : nothing}
      </tr>
    `;
  }
}
